import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cn } from '../lib/cn.js';
import { AnchoredList, useAnchor } from './anchored-list.js';
import { GRID_CELL_CONTROL } from './EntryGrid.js';


export interface ComboboxOption {
  value: string;
  label: string;
  hint?: string;
  group?: string;
  disabled?: boolean;
  keywords?: string;
}

export interface ComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly ComboboxOption[];
  placeholder?: string;
  emptyText?: string;
  caption?: string;
  disabled?: boolean;
  invalid?: boolean;
  variant?: 'grid' | 'field';
  className?: string;
  id?: string;
  'aria-label'?: string;
}

const FIELD_CONTROL = cn(
  'h-8 w-full rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--surface)]',
  'px-2.5 text-[12.5px] text-[var(--ink)] outline-none',
  'transition-colors duration-[var(--dur-fast)]',
  'focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-soft)]',
  'disabled:cursor-not-allowed disabled:bg-[var(--surface-2)] disabled:text-[var(--ink-3)]',
  'aria-[invalid=true]:border-[var(--debit)]',
);

function matches(option: ComboboxOption, terms: readonly string[]): boolean {
  if (terms.length === 0) return true;
  const hay = `${option.value} ${option.label} ${option.hint ?? ''} ${option.keywords ?? ''}`
    .toLowerCase()
    .replace(/\s+/g, ' ');
  return terms.every((term) => hay.includes(term));
}

export const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(function Combobox(
  {
    value,
    onChange,
    options,
    placeholder = 'Search…',
    emptyText = 'Nothing matches',
    caption,
    disabled,
    invalid,
    variant = 'grid',
    className,
    id,
    'aria-label': ariaLabel,
  },
  forwardedRef,
) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const { ref: inputRef, anchor } = useAnchor(open);
  const listRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement, []);

  const selected = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  const terms = useMemo(() => query.trim().toLowerCase().split(/\s+/).filter(Boolean), [query]);

  const shown = useMemo(() => options.filter((option) => matches(option, terms)), [options, terms]);

  const display = open ? query : (selected?.label ?? value);

  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActive(0);
  }, []);

  const commit = useCallback(
    (option: ComboboxOption | undefined) => {
      if (!option || option.disabled) return;
      onChange(option.value);
      close();
    },
    [onChange, close],
  );

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    const index = shown.findIndex((option) => option.value === value);
    setActive(index >= 0 ? index : 0);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        event.preventDefault();
        openList();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive((i) => nextEnabled(shown, i, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive((i) => nextEnabled(shown, i, -1));
        break;
      case 'Home':
        event.preventDefault();
        setActive(nextEnabled(shown, -1, 1));
        break;
      case 'End':
        event.preventDefault();
        setActive(nextEnabled(shown, shown.length, -1));
        break;
      case 'Enter':
        event.preventDefault();
        commit(shown[active]);
        break;
      case 'Tab':
        if (shown[active]) commit(shown[active]);
        else close();
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        close();
        break;
      default:
        break;
    }
  };

  const control = (
    <input
      ref={inputRef}
      id={id}
      type="text"
      role="combobox"
      autoComplete="off"
      spellCheck={false}
      aria-label={ariaLabel}
      aria-expanded={open}
      aria-controls={open ? listId : undefined}
      aria-autocomplete="list"
      aria-invalid={invalid || undefined}
      disabled={disabled}
      value={display}
      placeholder={open ? (selected?.label ?? placeholder) : placeholder}
      onChange={(event) => {
        setQuery(event.target.value);
        setActive(0);
        if (!open) setOpen(true);
      }}
      onMouseDown={() => {
        if (!open) openList();
      }}
      onFocus={() => {
        if (!open) openList();
      }}
      onBlur={(event) => {
        if (listRef.current?.contains(event.relatedTarget as Node)) return;
        close();
      }}
      onKeyDown={onKeyDown}
      className={cn(
        variant === 'grid' ? GRID_CELL_CONTROL : FIELD_CONTROL,
        'cursor-pointer',
        className,
      )}
    />
  );

  return (
    <>
      {control}
      {open && anchor && (
        <AnchoredList anchor={anchor} id={listId} listRef={listRef}>
          <>
            {caption && (
              <div className="px-2.5 pt-1 pb-1.5 text-[11px] text-[var(--ink-3)]">{caption}</div>
            )}

            {shown.length === 0 ? (
              <div className="px-2.5 py-2 text-[12px] text-[var(--ink-3)]">{emptyText}</div>
            ) : (
              shown.map((option, index) => {
                const previous = shown[index - 1];
                const heading = option.group && option.group !== previous?.group;

                return (
                  <div key={option.value}>
                    {heading && (
                      <div
                        className={cn(
                          'px-2.5 pt-2 pb-1 text-[10px] font-medium tracking-[0.08em] uppercase',
                          'text-[var(--ink-3)]',
                        )}
                      >
                        {option.group}
                      </div>
                    )}
                    <div
                      role="option"
                      data-index={index}
                      aria-selected={option.value === value}
                      aria-disabled={option.disabled || undefined}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => commit(option)}
                      className={cn(
                        'flex items-baseline gap-2 px-2.5 py-1.5 text-[12.5px]',
                        option.disabled
                          ? 'cursor-not-allowed text-[var(--ink-3)]'
                          : 'cursor-pointer text-[var(--ink)]',
                        index === active && !option.disabled && 'bg-[var(--accent-soft)]',
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate">{option.label}</span>
                      {option.hint && (
                        <span className="shrink-0 text-[11px] text-[var(--ink-3)]">
                          {option.hint}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </>
        </AnchoredList>
      )}
    </>
  );
});

function nextEnabled(options: readonly ComboboxOption[], from: number, direction: 1 | -1): number {
  for (let i = from + direction; i >= 0 && i < options.length; i += direction) {
    if (!options[i]?.disabled) return i;
  }
  return from >= 0 && from < options.length ? from : 0;
}
